import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { getAuth } from 'firebase/auth'
import sampleImage from '../Images/defprofile.png'

function VideoPrecheck() {
  const navigate = useNavigate()
  const location = useLocation()
  const auth = getAuth()

  const myData = location.state?.myData
  const startedBy = location.state?.startedBy || ''
  const startedByName = location.state?.startedByName || ''
  const channelName = location.state?.channelName || ''
  const otherUserId = location.state?.otherUserId || ''
  const otherUserName = location.state?.otherUserName || ''
  const otherUserCourse = location.state?.otherUserCourse || ''
  const otherUserAvatar = location.state?.otherUserAvatar || ''
  const otherUserPhone = location.state?.otherUserPhone || ''
  const workspaceCallId = location.state?.workspaceCallId || ''
  const workspaceCallRoomName = location.state?.workspaceCallRoomName || ''
  const workspaceCallRoomUrl = location.state?.workspaceCallRoomUrl || ''
  const workspaceCallJoinUrl = location.state?.workspaceCallJoinUrl || ''
  const isVideoCall = location.state?.isVideo !== false

  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const analyserRef = useRef(null)
  const audioContextRef = useRef(null)
  const animationRef = useRef(null)

  const [permissionError, setPermissionError] = useState('')
  const [devices, setDevices] = useState({ audioIn: [], videoIn: [], audioOut: [] })
  const [audioDeviceId, setAudioDeviceId] = useState('')
  const [videoDeviceId, setVideoDeviceId] = useState('')
  const [audioOutputId, setAudioOutputId] = useState('')
  const [audioLevel, setAudioLevel] = useState(0)
  const [isMuted, setIsMuted] = useState(false)
  const [isVideoOff, setIsVideoOff] = useState(false)

  const stopPreview = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    if (audioContextRef.current) {
      audioContextRef.current.close()
      audioContextRef.current = null
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current)
      animationRef.current = null
    }
  }

  const buildAudioMeter = (stream) => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)()
    const analyser = audioContext.createAnalyser()
    analyser.fftSize = 2048
    const source = audioContext.createMediaStreamSource(stream)
    source.connect(analyser)

    audioContextRef.current = audioContext
    analyserRef.current = analyser

    const dataArray = new Uint8Array(analyser.fftSize)
    const update = () => {
      analyser.getByteTimeDomainData(dataArray)
      let sumSquares = 0
      for (let i = 0; i < dataArray.length; i += 1) {
        const val = (dataArray[i] - 128) / 128
        sumSquares += val * val
      }
      const rms = Math.sqrt(sumSquares / dataArray.length)
      setAudioLevel(Math.min(1, rms * 2.4))
      animationRef.current = requestAnimationFrame(update)
    }

    update()
  }

  const loadDevices = async () => {
    const list = await navigator.mediaDevices.enumerateDevices()
    const audioIn = list.filter((item) => item.kind === 'audioinput')
    const videoIn = list.filter((item) => item.kind === 'videoinput')
    const audioOut = list.filter((item) => item.kind === 'audiooutput')
    setDevices({ audioIn, videoIn, audioOut })
    if (!audioDeviceId && audioIn[0]) setAudioDeviceId(audioIn[0].deviceId)
    if (!videoDeviceId && videoIn[0]) setVideoDeviceId(videoIn[0].deviceId)
    if (!audioOutputId && audioOut[0]) setAudioOutputId(audioOut[0].deviceId)
  }

  const startPreview = async (audioId, videoId) => {
    stopPreview()
    try {
      const constraints = {
        audio: audioId ? { deviceId: { exact: audioId } } : true,
        video: isVideoCall ? (videoId ? { deviceId: { exact: videoId } } : true) : false,
      }
      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }
      buildAudioMeter(stream)
      setPermissionError('')
    } catch (error) {
      console.error('Media permission error:', error)
      setPermissionError('Please allow camera and microphone access to continue.')
    }
  }

  useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setPermissionError('Your browser does not support camera/microphone access.')
      return () => {}
    }
    startPreview(audioDeviceId, isVideoCall ? videoDeviceId : '')
    loadDevices()

    navigator.mediaDevices.addEventListener('devicechange', loadDevices)
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', loadDevices)
      stopPreview()
    }
  }, [isVideoCall])

  useEffect(() => {
    if (!audioDeviceId && !videoDeviceId) return
    startPreview(audioDeviceId, isVideoCall ? videoDeviceId : '')
  }, [audioDeviceId, videoDeviceId, isVideoCall])

  useEffect(() => {
    if (!streamRef.current) return
    streamRef.current.getAudioTracks().forEach((track) => {
      track.enabled = !isMuted
    })
  }, [isMuted])

  useEffect(() => {
    if (!streamRef.current) return
    streamRef.current.getVideoTracks().forEach((track) => {
      track.enabled = !isVideoOff
    })
  }, [isVideoOff])

  useEffect(() => {
    if (!videoRef.current || !audioOutputId) return
    if (typeof videoRef.current.setSinkId !== 'function') return
    videoRef.current.setSinkId(audioOutputId).catch(() => {})
  }, [audioOutputId])

  const joinCall = () => {
    stopPreview()
    navigate('/video-call/room', {
      state: {
        myData,
        startedBy,
        startedByName,
        channelName,
        otherUserId,
        otherUserName,
        otherUserCourse,
        otherUserAvatar,
        otherUserPhone,
        workspaceCallId,
        workspaceCallRoomName,
        workspaceCallRoomUrl,
        workspaceCallJoinUrl,
        audioDeviceId,
        videoDeviceId,
        startMuted: isMuted,
        startVideoOff: !isVideoCall || isVideoOff,
      },
    })
  }

  return (
    <div className="videoPrecheckShell">
      <div className="videoPrecheckCard">
        <div className="videoPrecheckHeader">
          <div>
            <h3>Ready to join?</h3>
            <p>Check your camera and microphone before you enter the call.</p>
          </div>
          <div className="videoPrecheckUser">
            <img src={auth.currentUser?.photoURL || sampleImage} alt="" />
            <div>
              <span>{startedByName || auth.currentUser?.displayName || 'Student'}</span>
              <small>{channelName || myData || 'video call'}</small>
            </div>
          </div>
        </div>

        <div className="videoPrecheckBody">
          <div className="videoPrecheckPreview">
            <video ref={videoRef} autoPlay playsInline muted />
            {permissionError && <div className="videoPrecheckError">{permissionError}</div>}
            <div className="videoPrecheckToggles">
              <button
                type="button"
                className={isMuted ? 'off' : ''}
                onClick={() => setIsMuted((prev) => !prev)}
              >
                {isMuted ? 'Mic Off' : 'Mic On'}
              </button>
              {isVideoCall && (
                <button
                  type="button"
                  className={isVideoOff ? 'off' : ''}
                  onClick={() => setIsVideoOff((prev) => !prev)}
                >
                  {isVideoOff ? 'Camera Off' : 'Camera On'}
                </button>
              )}
            </div>
          </div>

          <div className="videoPrecheckSettings">
            <div className="videoPrecheckField">
              <label>Microphone</label>
              <select
                value={audioDeviceId}
                onChange={(event) => setAudioDeviceId(event.target.value)}
              >
                {devices.audioIn.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || 'Microphone'}
                  </option>
                ))}
              </select>
              <div className="videoPrecheckMeter">
                <span style={{ width: `${audioLevel * 100}%` }} />
              </div>
            </div>

            {isVideoCall && (
              <div className="videoPrecheckField">
                <label>Camera</label>
                <select
                  value={videoDeviceId}
                  onChange={(event) => setVideoDeviceId(event.target.value)}
                >
                  {devices.videoIn.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label || 'Camera'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="videoPrecheckField">
              <label>Speaker</label>
              <select
                value={audioOutputId}
                onChange={(event) => setAudioOutputId(event.target.value)}
              >
                {devices.audioOut.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || 'Speaker'}
                  </option>
                ))}
              </select>
              <small>Speaker selection may not be supported in all browsers.</small>
            </div>

            <button
              type="button"
              className="videoPrecheckJoin"
              onClick={joinCall}
              disabled={Boolean(permissionError)}
            >
              Join Call
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VideoPrecheck
